import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import _ from 'lodash'
import produce from 'immer'
import { useUpdateEffect } from 'react-use'

import { motion } from 'framer-motion'

import {
  RiArrowUpDownLine,
  RiArrowLeftRightLine,
  RiAddLine,
} from 'react-icons/ri'

import { useRecoilState, useRecoilValue, useRecoilCallback } from 'recoil'
import { layoutIds, layout } from '@/store/template/layout'
import { section } from '@/store/template/section'
import {
  undoHistory,
  undoHistoryAction,
  undoHistoryActionCount,
} from '@/store/builder/history'

import { grid_rows, grid_columns } from '@/store/template/settings'
import {
  screen,
  screenOpen,
  addSectionToBlankLayoutId,
} from '@/store/builder/store'
import { isBuilder } from '@/store/builder/store'

// components
import Section from '@/components/onepager/Section'

const LayoutBox = ({ layoutID, isSelecting, addBottomRow }) => {
  const allLayouts = useRecoilValue(layoutIds)
  const [getLayout, setLayout] = useRecoilState(layout(layoutID))
  const getSection = useRecoilValue(section(getLayout?.sectionID))
  const [getHistory, setHistory] = useRecoilState(undoHistory)
  const [getHistoryAction, setHistoryAction] = useRecoilState(undoHistoryAction)
  const [getHistoryActionCount, setHistoryActionCount] = useRecoilState(
    undoHistoryActionCount
  )
  const getIsBuilder = useRecoilValue(isBuilder)

  const gridRows = useRecoilValue(grid_rows)
  const gridColumns = useRecoilValue(grid_columns)
  const gridColumnsEnd = parseInt(gridColumns) + 1
  const gridRowsEnd = parseInt(gridRows) + 1
  const [isResizingEW, setIsResizingEW] = useState(false)
  const [isResizingNS, setIsResizingNS] = useState(false)
  const [cantMove, setCantMove] = useState(false)
  const [getSreen, setScreen] = useRecoilState(screen)
  const [getScreenOpen, setScreenOpen] = useRecoilState(screenOpen)
  const [addBlankID, setAddBlankID] = useRecoilState(addSectionToBlankLayoutId)
  const [sectionBGColor, setSectionBGColor] = useState(false)
  const [layouts, setLayouts] = useRecoilState(layoutIds)

  let dragImg = null

  useEffect(() => {
    dragImg = new Image(0, 0)
    dragImg.src =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
  })

  // undoSystem -- Layout System
  useUpdateEffect(async () => {
    if (!getHistoryAction) {
      setHistoryActionCount(getHistoryActionCount + 1)
    } else {
      setHistoryAction(false)
    }
  }, [getLayout])

  useEffect(() => {
    if (getSection.bg_color === undefined) {
      setSectionBGColor(false)
    } else {
      setSectionBGColor(getSection.bg_color)
    }
  }, [getSection])

  const checkTopEdge = () => getLayout.gridarea['grid-row-start'] == 1
  const checkLeftEdge = () => getLayout.gridarea['grid-column-start'] == 1

  const checkRightEdge = () =>
    getLayout.gridarea['grid-column-end'] == gridColumnsEnd

  const checkBottomEdge = () =>
    getLayout.gridarea['grid-row-end'] == gridRowsEnd

  const checkTopRightCorner = () =>
    getLayout.gridarea['grid-row-start'] == 1 &&
    getLayout.gridarea['grid-column-end'] == gridColumnsEnd
  const checkTopLeftCorner = () =>
    getLayout.gridarea['grid-row-start'] == 1 &&
    getLayout.gridarea['grid-column-start'] == 1
  const checkBottomLeftCorner = () =>
    getLayout.gridarea['grid-row-end'] == gridRowsEnd &&
    getLayout.gridarea['grid-column-start'] == 1
  const checkBottomRightCorner = () =>
    getLayout.gridarea['grid-row-end'] == gridRowsEnd &&
    getLayout.gridarea['grid-column-end'] == gridColumnsEnd

  // const checkIsNotRightEdge = () =>
  //   getLayout.gridarea['grid-column-end'] !== gridColumnsEnd
  // const checkIsNotBottomEdge = () =>
  //   getLayout.gridarea['grid-row-end'] !== gridRowsEnd

  const checkIsNotRightEdge = () => {
    return true
  }
  const checkIsNotBottomEdge = () => {
    return true
  }

  // Dynamic classes
  function dynamicClasses() {
    return classNames(
      `box flex flex-col w-full h-full relative sectionType_${getSection.type}`,
      {
        hasTopBorder: checkTopEdge(),
        hasLeftBorder: checkLeftEdge(),
        hasRightBorder: checkRightEdge(),
        hasBottomBorder: checkBottomEdge(),
        hasTopRightRadius: checkTopRightCorner(),
        hasTopLeftRadius: checkTopLeftCorner(),
        hasBottomLeftRadius: checkBottomLeftCorner(),
        hasBottomRightRadius: checkBottomRightCorner(),
        hasCustomBGColor: getSection.bg_color !== 'auto' && getSection.bg_color,
        hasCustomBGColorInhert: getSection.bg_color == 'auto',
        sectionWhiteText: getSection.text_color == 'white',
      }
    )
  }

  const gridPlacement = {
    gridRowStart: getLayout.gridarea['grid-row-start'],
    gridRowEnd: getLayout.gridarea['grid-row-end'],
    gridColumnStart: getLayout.gridarea['grid-column-start'],
    gridColumnEnd: getLayout.gridarea['grid-column-end'],
    backgroundColor: sectionBGColor,
  }

  // Delete blank layout

  // function deleteLayout() {
  // }

  const deleteLayout = useRecoilCallback(({ snapshot, set }) => () => {
    const nextState = produce(allLayouts, draft => {
      const index = draft.findIndex(id => id === layoutID)
      if (index !== -1) {
        draft.splice(index, 1)
      }
    })
    set(layoutIds, nextState)
  })

  // Resizing

  const layoutResizeToLeftRight = useRecoilCallback(
    ({ snapshot, set }) =>
      move => {
        const rootLayoutID = layoutID
        const gridColEnd = getLayout.gridarea['grid-column-end']
        const gridRowStart = getLayout.gridarea['grid-row-start']
        const gridRowEnd = parseInt(getLayout.gridarea['grid-row-end']) - 1
        let gridRowSpread = []
        for (var i = gridRowStart; i <= gridRowEnd; i++) {
          gridRowSpread.push(i)
        }

        let allLeftLayouts = []
        let allRightLayouts = []
        let approvedLeftLayouts = [rootLayoutID]
        let approvedRightLayouts = []
        let approvedRightLayoutExtensions = []

        // FIND ALL LEFT & RIGHT
        _.forEach(allLayouts, function (layoutID, index) {
          const layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
          // set allRightLayouts
          if (layoutAtom.gridarea['grid-column-start'] == gridColEnd) {
            allRightLayouts.push({
              id: layoutID,
              gridarea: layoutAtom.gridarea,
              rowZone: getRowZone(layoutAtom.gridarea),
              rowCount: layoutAtom.gridarea['grid-row-end'],
            })
          }
          // set allLeftLayouts
          if (layoutAtom.gridarea['grid-column-end'] == gridColEnd) {
            // check if its root, ignore
            if (layoutID !== rootLayoutID) {
              allLeftLayouts.push({
                id: layoutID,
                gridarea: layoutAtom.gridarea,
                rowZone: getRowZone(layoutAtom.gridarea),
                rowCount: layoutAtom.gridarea['grid-row-end'],
              })
            }
          }
        })

        allLeftLayouts = _.sortBy(allLeftLayouts, 'rowCount')
        allRightLayouts = _.sortBy(allRightLayouts, 'rowCount')

        // console.log('Left: ', allLeftLayouts);
        // console.log('Right: ', allRightLayouts)

        // find initial approvedRightLayouts (within same row zone as this.layout)
        _.forEach(allRightLayouts, function (layoutObject) {
          const { inSameZone, doesExtend } = isWithinSameRowZone(
            layoutObject.rowZone,
            gridRowSpread
          )

          if (inSameZone) {
            approvedRightLayouts.push(layoutObject.id)
            if (doesExtend) {
              approvedRightLayoutExtensions.push(layoutObject)
            }
          }
        })

        // if any below extends - loop & find all approved above and below
        if (approvedRightLayoutExtensions.length > 0) {
          // loop through left layouts, top to down
          _.forEach(allLeftLayouts, function (leftLayoutObject) {
            let isThisApproved = false
            let rightInSameZone = []
            // find if this has any approved right in row zone
            _.forEach(allRightLayouts, function (rightLayoutObject) {
              // find ones that are in row zone
              const { inSameZone } = isWithinSameRowZone(
                leftLayoutObject.rowZone,
                rightLayoutObject.rowZone
              )
              if (inSameZone) {
                // console.log(_.findIndex(approvedRightLayouts, function (o) { return o == rightLayoutObject.id }));
                // check if this below is approved
                if (
                  _.findIndex(approvedRightLayouts, function (o) {
                    return o == rightLayoutObject.id
                  }) !== -1
                ) {
                  isThisApproved = true
                  approvedLeftLayouts.push(leftLayoutObject.id)
                } else {
                  rightInSameZone.push(rightLayoutObject.id)
                }
              }
            })

            if (isThisApproved) {
              _.forEach(rightInSameZone, function (layoutID) {
                approvedRightLayouts.push(layoutID)
              })
            }
          })

          // loop again
          _.forEach(allLeftLayouts, function (leftLayoutObject) {
            let isThisApproved = false
            let rightInSameZone = []
            // find if this has any approved right in row zone
            _.forEach(allRightLayouts, function (rightLayoutObject) {
              // find ones that are in row zone
              const { inSameZone } = isWithinSameRowZone(
                leftLayoutObject.rowZone,
                rightLayoutObject.rowZone
              )
              if (inSameZone) {
                // console.log(_.findIndex(approvedRightLayouts, function (o) { return o == rightLayoutObject.id }));
                // check if this below is approved
                if (
                  _.findIndex(approvedRightLayouts, function (o) {
                    return o == rightLayoutObject.id
                  }) !== -1
                ) {
                  isThisApproved = true
                  approvedLeftLayouts.push(leftLayoutObject.id)
                } else {
                  rightInSameZone.push(rightLayoutObject.id)
                }
              }
            })

            if (isThisApproved) {
              _.forEach(rightInSameZone, function (layoutID) {
                approvedRightLayouts.push(layoutID)
              })
            }
          })
        }

        // remove dup ids
        approvedRightLayouts = [...new Set(approvedRightLayouts)]
        approvedLeftLayouts = [...new Set(approvedLeftLayouts)]

        // console.log('-----');
        // console.log('Approved left: ', approvedLeftLayouts);
        // console.log('Approved right: ', approvedRightLayouts);

        // check which direction to move
        if (move == 'right') {
          let safeToMove = true

          // check if any of the shrink these, can be shrunk
          _.forEach(approvedRightLayouts, function (layoutID, index) {
            let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents

            // check if this layout can be made smaller (if it already is 1x1 then safeToMove False)
            const thisColStart =
              parseInt(layoutAtom.gridarea['grid-column-start']) + 1
            const thisColEnd = parseInt(layoutAtom.gridarea['grid-column-end'])
            if (thisColStart == thisColEnd) {
              safeToMove = false
            }
          })

          if (safeToMove) {
            setCantMove(false)

            // shrink to the right
            _.forEach(approvedRightLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-column-start'] =
                  parseInt(draft.gridarea['grid-column-start']) + 1
              })
              set(layout(layoutID), nextState)
            })

            // grow to the left
            _.forEach(approvedLeftLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-column-end'] =
                  parseInt(draft.gridarea['grid-column-end']) + 1
              })
              set(layout(layoutID), nextState)
            })
          } else {
            setCantMove(true)
          }
        } else {
          // move to the left

          let safeToMove = true

          // check if any of the shrink these, can be shrunk
          _.forEach(approvedLeftLayouts, function (layoutID, index) {
            let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents

            // check if this layout can be made smaller (if it already is 1x1 then safeToMove False)
            const thisColStart =
              parseInt(layoutAtom.gridarea['grid-column-start']) + 1
            const thisColEnd = parseInt(layoutAtom.gridarea['grid-column-end'])
            if (thisColStart == thisColEnd) {
              safeToMove = false
            }
          })

          if (safeToMove) {
            setCantMove(false)

            // grow to the right
            _.forEach(approvedRightLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-column-start'] =
                  parseInt(draft.gridarea['grid-column-start']) - 1
              })
              set(layout(layoutID), nextState)
            })

            // shrink to the left
            _.forEach(approvedLeftLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-column-end'] =
                  parseInt(draft.gridarea['grid-column-end']) - 1
              })
              set(layout(layoutID), nextState)
            })
          } else {
            setCantMove(true)
          }
        }
      }
  )

  const layoutResizeToUpDown = useRecoilCallback(
    ({ snapshot, set }) =>
      move => {
        const rootLayoutID = layoutID
        const gridRowStart = getLayout.gridarea['grid-row-start']
        const gridRowEnd = getLayout.gridarea['grid-row-end']
        const gridColStart = getLayout.gridarea['grid-column-start']
        const gridColEnd = parseInt(getLayout.gridarea['grid-column-end']) - 1

        let rootColumnZone = []
        for (var i = gridColStart; i <= gridColEnd; i++) {
          rootColumnZone.push(i)
        }

        let allAboveLayouts = []
        let allBelowLayouts = []
        let approvedAboveLayouts = [rootLayoutID]
        let approvedBelowLayouts = []
        let approvedBelowLayoutExtensions = []

        // FIND ALL ABOVE & BELOW
        _.forEach(allLayouts, function (layoutID, index) {
          const layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
          // set allBelowLayouts
          if (layoutAtom.gridarea['grid-row-start'] == gridRowEnd) {
            allBelowLayouts.push({
              id: layoutID,
              gridarea: layoutAtom.gridarea,
              columnZone: getColumnZone(layoutAtom.gridarea),
              columnCount: layoutAtom.gridarea['grid-column-end'],
            })
          }
          // set allAboveLayouts
          if (layoutAtom.gridarea['grid-row-end'] == gridRowEnd) {
            // check if its root, ignore
            if (layoutID !== rootLayoutID) {
              allAboveLayouts.push({
                id: layoutID,
                gridarea: layoutAtom.gridarea,
                columnZone: getColumnZone(layoutAtom.gridarea),
                columnCount: layoutAtom.gridarea['grid-column-end'],
              })
            }
          }
        })

        allAboveLayouts = _.sortBy(allAboveLayouts, 'columnCount')
        allBelowLayouts = _.sortBy(allBelowLayouts, 'columnCount')

        // find initial approvedBelowLayouts(within same column zone as this.layout)
        _.forEach(allBelowLayouts, function (layoutObject, index) {
          const { inSameZone, doesExtend } = isWithinSameColumnZone(
            layoutObject.columnZone,
            rootColumnZone
          )
          if (inSameZone) {
            approvedBelowLayouts.push(layoutObject.id)
            // console.log('found this one in zone: ', layoutObject.id, doesExtend);
            if (doesExtend) {
              approvedBelowLayoutExtensions.push(layoutObject)
            }
          }
        })

        // if any below extends - loop & find all approved above and below
        if (approvedBelowLayoutExtensions.length > 0) {
          // loop through above layouts, left to right, and check for
          _.forEach(allAboveLayouts, function (aboveLayoutObject) {
            let isThisApproved = false
            let belowInSameZone = []
            // find if this has any approved below in column zone
            _.forEach(allBelowLayouts, function (belowLayoutObject) {
              // find ones that are in column zone
              const { inSameZone } = isWithinSameColumnZone(
                aboveLayoutObject.columnZone,
                belowLayoutObject.columnZone
              )
              if (inSameZone) {
                // console.log(_.findIndex(approvedBelowLayouts, function (o) { return o == belowLayoutObject.id }));
                // check if this below is approved
                if (
                  _.findIndex(approvedBelowLayouts, function (o) {
                    return o == belowLayoutObject.id
                  }) !== -1
                ) {
                  isThisApproved = true
                  approvedAboveLayouts.push(aboveLayoutObject.id)
                } else {
                  belowInSameZone.push(belowLayoutObject.id)
                }
              }
            })

            if (isThisApproved) {
              _.forEach(belowInSameZone, function (layoutID) {
                approvedBelowLayouts.push(layoutID)
              })
            }
          })

          // go through left to right - AGAIN - find any missing
          _.forEach(allAboveLayouts, function (aboveLayoutObject) {
            let isThisApproved = false
            let belowInSameZone = []
            // find if this has any approved below in column zone
            _.forEach(allBelowLayouts, function (belowLayoutObject) {
              // find ones that are in column zone
              const { inSameZone } = isWithinSameColumnZone(
                aboveLayoutObject.columnZone,
                belowLayoutObject.columnZone
              )
              if (inSameZone) {
                // console.log(_.findIndex(approvedBelowLayouts, function (o) { return o == belowLayoutObject.id }));
                // check if this below is approved
                if (
                  _.findIndex(approvedBelowLayouts, function (o) {
                    return o == belowLayoutObject.id
                  }) !== -1
                ) {
                  isThisApproved = true
                  approvedAboveLayouts.push(aboveLayoutObject.id)
                } else {
                  belowInSameZone.push(belowLayoutObject.id)
                }
              }
            })

            if (isThisApproved) {
              _.forEach(belowInSameZone, function (layoutID, index) {
                approvedBelowLayouts.push(layoutID)
              })
            }
          })
        }

        // remove dup ids
        approvedAboveLayouts = [...new Set(approvedAboveLayouts)]
        approvedBelowLayouts = [...new Set(approvedBelowLayouts)]

        // console.log('-----');
        // console.log('all approved above: ', approvedAboveLayouts);
        // console.log('all approved below: ', approvedBelowLayouts);

        if (move == 'down') {
          // console.log('DOWN');

          let safeToMove = true

          // check if any below can shrink
          _.forEach(approvedBelowLayouts, function (layoutID, index) {
            let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
            // check if this layout can be made smaller (if it already is 1x1 then safeToMove False)
            const thisColStart =
              parseInt(layoutAtom.gridarea['grid-row-start']) + 1
            const thisColEnd = parseInt(layoutAtom.gridarea['grid-row-end'])
            if (thisColStart == thisColEnd) {
              safeToMove = false
            }
          })

          if (safeToMove) {
            setCantMove(false)

            // shrink below
            _.forEach(approvedBelowLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-row-start'] =
                  parseInt(draft.gridarea['grid-row-start']) + 1
              })
              set(layout(layoutID), nextState)
            })

            // grow above
            _.forEach(approvedAboveLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-row-end'] =
                  parseInt(draft.gridarea['grid-row-end']) + 1
              })
              set(layout(layoutID), nextState)
            })
          } else {
            // console.log('cant make smaller');
            setCantMove(true)
          }
        } else {
          // console.log('UP');

          let safeToMove = true

          // check if any below can shrink
          _.forEach(approvedAboveLayouts, function (layoutID, index) {
            let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents

            // check if this layout can be made smaller (if it already is 1x1 then safeToMove False)
            const thisColStart =
              parseInt(layoutAtom.gridarea['grid-row-start']) + 1
            const thisColEnd = parseInt(layoutAtom.gridarea['grid-row-end'])
            if (thisColStart == thisColEnd) {
              safeToMove = false
            }
          })

          if (safeToMove) {
            setCantMove(false)

            // shrink above
            _.forEach(approvedAboveLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-row-end'] =
                  parseInt(draft.gridarea['grid-row-end']) - 1
              })
              set(layout(layoutID), nextState)
            })

            // shrink below
            _.forEach(approvedBelowLayouts, function (layoutID, index) {
              let layoutAtom = snapshot.getLoadable(layout(layoutID)).contents
              const nextState = produce(layoutAtom, draft => {
                draft.gridarea['grid-row-start'] =
                  parseInt(draft.gridarea['grid-row-start']) - 1
              })
              set(layout(layoutID), nextState)
            })
          } else {
            // console.log('cant make smaller');
            setCantMove(true)
          }
        }
      }
  )

  // get column zone array
  const getColumnZone = gridarea => {
    const gridColStart = parseInt(gridarea['grid-column-start'])
    const gridColEnd = parseInt(gridarea['grid-column-end']) - 1
    let gridColSpread = []
    for (var i = gridColStart; i <= gridColEnd; i++) {
      gridColSpread.push(i)
    }
    return gridColSpread
  }

  const isWithinSameColumnZone = (thisColZone, rootColZone) => {
    let inSameZone = false
    let doesExtend = false
    // check if this is within same zone as root
    if (_.intersection(thisColZone, rootColZone).length > 0) {
      inSameZone = true
      let thisColLength = thisColZone.length - 1
      let rootColLength = rootColZone.length - 1
      // is that start of this lower than root start? (starts sooner)
      if (rootColZone[0] > thisColZone[0]) {
        doesExtend = true
      }
      // is the column end this higher than root?
      if (rootColZone[rootColLength] < thisColZone[thisColLength]) {
        doesExtend = true
      }
    }
    return { inSameZone, doesExtend }
  }

  // get row zone array
  const getRowZone = gridarea => {
    const gridRowStart = parseInt(gridarea['grid-row-start'])
    const gridRowEnd = parseInt(gridarea['grid-row-end']) - 1
    let gridRowSpread = []
    for (var i = gridRowStart; i <= gridRowEnd; i++) {
      gridRowSpread.push(i)
    }
    return gridRowSpread
  }

  const isWithinSameRowZone = (thisRowZone, rootRowZone) => {
    let inSameZone = false
    let doesExtend = false
    // check if this is within same zone as root
    if (_.intersection(thisRowZone, rootRowZone).length > 0) {
      inSameZone = true
      let thisRowLength = thisRowZone.length - 1
      let rootRowLength = rootRowZone.length - 1
      // is that start of this lower than root start? (starts sooner)
      if (rootRowZone[0] > thisRowZone[0]) {
        doesExtend = true
      }
      // is the row end this higher than root?
      if (rootRowZone[rootRowLength] < thisRowZone[thisRowLength]) {
        doesExtend = true
      }
    }
    return { inSameZone, doesExtend }
  }

  const findAnyAboveLayouts = (allAbove, allBelowExtensions, ignoreTheses) => {
    let anyFound = []

    // loop through allAboveLayouts
    _.forEach(allAbove, function (layoutObject, index) {
      // ignore approvedLayouts
      if (ignoreTheses.findIndex(id => id == layoutObject.id)) {
        // check if this is in the same zone as any of the approvedBelowExtenstions
        _.forEach(allBelowExtensions, function (layoutBelowExtension, index) {
          // get info on this layout
          const { inSameZone, doesExtend } = isWithinSameColumnZone(
            layoutBelowExtension.columnZone,
            layoutObject.columnZone
          )

          // check if its in the same zone (approve)
          if (inSameZone) {
            anyFound.push(layoutObject)
          }
        })
      }
    })

    return anyFound
  }

  const findAnyBelowLayouts = (newAbove, allBelow, ignoreTheses) => {
    let anyFound = []

    // loop through newAbove
    _.forEach(newAbove, function (layoutObject, index) {
      // check if this is in the same zone as any of the allBelow
      _.forEach(allBelow, function (layoutBelow, index) {
        // ignore approvedBelowLayouts
        if (ignoreTheses.findIndex(id => id == layoutBelow.id)) {
          // get info on this layout
          const { inSameZone, doesExtend } = isWithinSameColumnZone(
            layoutBelow.columnZone,
            layoutObject.columnZone
          )

          // check if its in the same zone (approve)
          if (inSameZone) {
            anyFound.push(layoutBelow)
            // console.log('Layout extends?', layoutObject, doesExtend);
          }
        }
      })
    })

    return anyFound
  }

  function isBetween(x, min, max) {
    return x >= min && x <= max
  }

  const getLayoutInfo = useRecoilCallback(({ snapshot }) => layoutID => {
    return snapshot.getLoadable(layout(layoutID)).contents
  })

  const shiftRowDown = useRecoilCallback(({ snapshot, set }) => layoutID => {
    const getThisContent = getLayoutInfo(layoutID)
    const nextState = produce(getThisContent, draft => {
      draft.gridarea['grid-row-start'] =
        parseInt(draft.gridarea['grid-row-start']) + 1
      draft.gridarea['grid-row-end'] =
        parseInt(draft.gridarea['grid-row-end']) + 1
    })
    set(layout(layoutID), nextState)
  })

  function addRow(layoutID) {
    addBottomRow()
    let currentRowEnd = null
    layouts.map((checkLayoutID, index) => {
      if (layoutID == checkLayoutID) {
        const data = getLayoutInfo(checkLayoutID)
        currentRowEnd = data.gridarea['grid-row-end']
      }
    })

    layouts.map((nextLayoutID, index) => {
      const data = getLayoutInfo(nextLayoutID)
      if (data.gridarea['grid-row-end'] > currentRowEnd) {
        shiftRowDown(nextLayoutID)
      }
    })
  }

  return (
    <div className={dynamicClasses()} style={gridPlacement}>
      {getLayout.sectionID && <Section id={getLayout.sectionID} />}

      {!getLayout.sectionID && getIsBuilder && (
        <div className="h-full flex flex-col items-center justify-center">
          <div
            onClick={() => {
              setScreen('addsection')
              setScreenOpen(true)
              setAddBlankID(layoutID)
            }}
            className=" cursor-pointer text-gray-400 bg-gray-50 hover:bg-blue-50 border-2 border-dashed border-gray-300 hover:text-blue-500 hover:border-blue-400 group rounded py-5 px-5 flex flex-col items-center relative justify-center"
          >
            <div className="text-6xl ">
              <RiAddLine />
            </div>
            {/* <h3 className="font-medium text-lg hidden group-hover:block">Add Section</h3> */}
            {/* <p className="font-medium text-xs opacity-75 ">Add Section</p> */}
          </div>

          <div
            onClick={() => {
              deleteLayout()
            }}
            className="mt-2"
          >
            <button className="font-bold text-xs text-gray-400 bg-gray-50 hover:bg-red-400 hover:text-red-100 rounded py-2 px-4">
              remove
            </button>
          </div>
        </div>
      )}

      {getIsBuilder && !isSelecting && (
        <div
          className={classNames('resizeHorz', 'z-50', {
            resizeHorzResizing: isResizingEW,
            cantMove: isResizingEW && cantMove,
          })}
        >
          <div className="resizeHorzLine" />
          <div className="resizeHorzItems">
            <div
              draggable
              onDragStart={e => {
                e.dataTransfer.setDragImage(dragImg, 0, 0)
              }}
              onDrag={e => {
                setIsResizingEW(true)

                var rect = e.target.getBoundingClientRect()
                var x = e.clientX - rect.left
                var y = e.clientY - rect.top
                const gridStops = 1280 / parseInt(gridColumns)

                if (x < 0) {
                  if (
                    isBetween(Math.round(Math.abs(x)), gridStops / 2, gridStops)
                  ) {
                    layoutResizeToLeftRight('left')
                  }
                } else {
                  if (isBetween(Math.round(x), gridStops / 2, gridStops)) {
                    layoutResizeToLeftRight('right')
                  }
                }
              }}
              onDragEnd={() => {
                setIsResizingEW(false)
                setCantMove(false)
              }}
              className="resizeHorzItem shadow"
            >
              <RiArrowLeftRightLine />
            </div>
          </div>
        </div>
      )}
      {getIsBuilder && !isSelecting && (
        <div
          className={classNames('resizeVert', 'z-50', {
            resizeVertResizing: isResizingNS,
            cantMove: isResizingNS && cantMove,
          })}
        >
          <div className="resizeVertLine" />
          <div className="resizeVertItems flex items-center">
            <div
              draggable
              onDragStart={e => {
                e.dataTransfer.setDragImage(dragImg, 0, 0)
              }}
              onDrag={e => {
                setIsResizingNS(true)

                var rect = e.target.getBoundingClientRect()
                var x = e.clientX - rect.left
                var y = e.clientY - rect.top
                const gridStops = 1280 / parseInt(gridRows)

                if (y < 0) {
                  if (
                    isBetween(Math.round(Math.abs(y)), gridStops / 2, gridStops)
                  ) {
                    layoutResizeToUpDown('up')
                  }
                } else {
                  if (isBetween(Math.round(y), gridStops / 2, gridStops)) {
                    layoutResizeToUpDown('down')
                  }
                }
              }}
              onDragEnd={() => {
                setIsResizingNS(false)
                setCantMove(false)
              }}
              className="resizeVertItem shadow"
            >
              <RiArrowUpDownLine />
            </div>
          </div>

          <div className="addVertRows flex items-center">
            <div
              onClick={() => {
                addRow(layoutID)
              }}
              className="addVertRow shadow"
            >
              <RiAddLine />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LayoutBox
